/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import _ from 'lodash';
import { NormalizedPatient, normalizePatient } from '../../../models/patient';
import { Patient } from '../../../backendModels/patient.model';
import { NormalizedMission, normalizeMissionData, sanitizeMission } from '../../../models/operational';
import { MissionData } from '../../../backendModels/mission-data.model';
import { controlCenterPropertiesOfMissionDataForPatientMaster } from '../PatientMasterData/PatientMasterData.functions';

/*
Currently, selection of participants cannot be synchronized back to mission (no entity existing to store this data yet).
Calculating diffs would always show changes as soon as user changed participants in DIVI-UI. E.g. switching mission and
back will always show changes to be imported -> ignore for the moment.
 */
export const excludedParticipantsProperties = ['participants'];

/*
Get relevant properties of mission data displayed in DIVI's Operational ("Einsatztechnische Daten") section. Is easier
to define as exclude than to pick all missionLocation properties and transportDestination; must not include the mission
data (control center) displayed in PatientMaster.
 */
export const excludedMissionDataPropertiesForOperational = [
  ...controlCenterPropertiesOfMissionDataForPatientMaster,
  ...excludedParticipantsProperties,
];

const diviOrder: string[] = [
  'participants',
  'missionStreetGPSLatitude',
  'missionCityGPSLongitude',
  'missionPostcode',
  'missionLocationType',
  'symptomOnset',
  'transportLocation',
];

export function compareByDiviOrder(a: [string, string | number | null], b: [string, string | number | null]) {
  return diviOrder.indexOf(a[0]) - diviOrder.indexOf(b[0]);
}

export function calculatePatientDiff(patient: Patient | undefined, lastSyncedPatient: NormalizedPatient | undefined) {
  return _.differenceWith(
    _.toPairs(normalizePatient(_.pick(patient, ['symptomOnset']))),
    _.toPairs(normalizePatient(_.pick(lastSyncedPatient, ['symptomOnset']))),
    _.isEqual
  );
}

export function calculateMissionDataDiff(
  missionData: MissionData | undefined,
  lastSynchronizedMissionData: NormalizedMission | undefined
) {
  return _.differenceWith(
    _.toPairs(normalizeMissionData(_.omit(missionData, ['controlCenter', ...excludedParticipantsProperties]))),
    _.toPairs(sanitizeMission(_.omit(lastSynchronizedMissionData, excludedMissionDataPropertiesForOperational))),
    _.isEqual
  );
}
